import debounce from "lodash/debounce";
import { useCallback, useEffect, useRef } from "react";

/**
 * Per-key debounced save. Useful for inline-editable grids / forms where edits on
 * multiple distinct rows should each debounce independently before firing to the backend.
 *
 * - Each call site key gets its own debounce timer (via a useRef Map).
 * - Pending saves are flushed on unmount so navigation never loses data.
 * - `saveFn` is read via a ref, so it can close over fresh state without resetting timers.
 *
 * @param saveFn Called with the latest payload after `delay` ms of inactivity per key.
 * @param delay Debounce window in ms. Default 3250ms.
 * @returns A `schedule(key, payload)` function. Call repeatedly; only the last payload per key fires.
 */
export function useDebouncedSave<TPayload>(saveFn: (payload: TPayload) => void, delay = 3250) {
	const saveFnRef = useRef(saveFn);
	saveFnRef.current = saveFn;

	const debouncedMap = useRef(new Map<string, ReturnType<typeof debounce<(p: TPayload) => void>>>());

	const schedule = useCallback(
		(key: string, payload: TPayload) => {
			let fn = debouncedMap.current.get(key);
			if (!fn) {
				fn = debounce((p: TPayload) => saveFnRef.current(p), delay);
				debouncedMap.current.set(key, fn);
			}
			fn(payload);
		},
		[delay],
	);

	useEffect(
		// Flush all pending saves on unmount to avoid data loss
		() => () => {
			for (const fn of debouncedMap.current.values()) {
				fn.flush();
			}
			debouncedMap.current.clear();
		},
		[],
	);

	return schedule;
}
